import React, { useEffect, useState } from "react";
import { Scissors, Sparkles, Download, Flame } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useLang } from "@/i18n";

const PLAT_COLOR = { TikTok: "text-fuchsia-400", Reels: "text-pink-400", Shorts: "text-red-400" };

export default function Clips() {
  const { t } = useLang();
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);

  useEffect(() => { api.get("/sessions").then((r) => { const ended = r.data.find((s) => s.status === "ended") || r.data[0]; setSession(ended); }); }, []);

  const generate = async () => {
    if (!session) return;
    setLoading(true);
    try { const r = await api.post("/ai/clips", { session_id: session.id }); setClips(r.data.clips || []); toast.success(`${r.data.clips.length} clips generated`); }
    catch (e) { toast.error("Clip generation failed"); } finally { setLoading(false); }
  };

  return (
    <AppLayout active="clips">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2"><Scissors className="h-6 w-6 text-cyan-400" /> AI Clip Generator</h1>
          <p className="text-sm text-muted-foreground mt-1">One 2-hour live → shorts, Reels & TikTok candidates{session ? ` · ${session.title}` : ""}</p>
        </div>
        <button data-testid="generate-clips-btn" onClick={generate} disabled={loading} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-bold disabled:opacity-60">
          <Sparkles className="h-4 w-4" /> {loading ? "Analyzing live…" : t("generate_clips")}
        </button>
      </div>

      {clips.length === 0 && !loading && (
        <div className="rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground">
          <Scissors className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No clips yet. Generate AI clips from your latest live.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {clips.map((c) => (
          <div key={c.id} data-testid={`clip-${c.id}`} className="rounded-xl border border-border bg-card overflow-hidden fade-in">
            <div className="relative aspect-video bg-gradient-to-br from-[#11141C] to-[#1b2030] flex items-center justify-center">
              <Scissors className="h-8 w-8 text-cyan-400/60" />
              <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded bg-black/60 text-white text-[10px] font-mono"><Flame className="h-3 w-3 text-amber-400" /> {c.hook_score}/100</div>
              <div className={`absolute top-2 right-2 px-2 py-1 rounded bg-black/60 text-[10px] font-mono ${PLAT_COLOR[c.platform] || "text-white"}`}>{c.platform}</div>
              <div className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/60 text-white text-[10px] font-mono">{c.duration}s</div>
            </div>
            <div className="p-4">
              <div className="text-sm font-bold">{c.title}</div>
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.caption}</div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(c.hashtags || []).map((h) => <span key={h} className="text-[10px] font-mono text-cyan-400">{h.startsWith("#") ? h : "#" + h}</span>)}
              </div>
              <button data-testid={`export-clip-${c.id}`} onClick={() => toast.success(`Exported "${c.title}" to ${c.platform}`)} className="mt-3 w-full h-9 rounded-lg bg-accent hover:bg-cyan-600 hover:text-white text-xs font-semibold flex items-center justify-center gap-1 transition-colors"><Download className="h-3.5 w-3.5" /> Export to {c.platform}</button>
            </div>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
